import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../services/auth.service';
import { MusicSyncService } from '../../services/music-sync.service';
import { APP_CONSTANTS } from '../../constants/app.constants';

@Component({
  selector: 'app-login-callback',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="callback-container">
      <div class="callback-content">
        <div class="loading-spinner"></div>
        <h2>{{ loadingMessage }}</h2>
        <p class="callback-status">{{ statusMessage }}</p>
        <div class="error-message" *ngIf="errorMessage">
          <p>{{ errorMessage }}</p>
          <button class="retry-btn" (click)="goToLogin()">
            Back to Login
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .callback-container {
      min-height: 100vh;
      background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
      color: #e2e8f0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    }

    .callback-content {
      text-align: center;
      max-width: 400px;
      padding: 2rem;
    }

    .loading-spinner {
      width: 50px;
      height: 50px;
      border: 4px solid rgba(120, 119, 198, 0.3);
      border-top: 4px solid #7877c6;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 2rem;
    }

    h2 {
      font-size: 1.5rem;
      font-weight: 600;
      color: #ffffff;
      margin-bottom: 1rem;
    }

    .callback-status {
      color: #a0aec0;
      margin-bottom: 2rem;
      line-height: 1.6;
    }

    .error-message {
      background: rgba(239, 68, 68, 0.1);
      border: 1px solid rgba(239, 68, 68, 0.3);
      border-radius: 0.5rem;
      padding: 1rem;
      color: #ef4444;

      .retry-btn {
        background: #7877c6;
        border: none;
        color: #ffffff;
        padding: 0.75rem 1.5rem;
        border-radius: 0.5rem;
        font-weight: 600;
        cursor: pointer;
        margin-top: 1rem;
        transition: all 0.2s ease;

        &:hover {
          background: #9896f0;
        }
      }
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `]
})
export class LoginCallbackComponent implements OnInit {
  statusMessage: string = '';
  errorMessage: string = '';
  isLoading: boolean = false;
  loadingMessage = 'Processing login...';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private musicSync: MusicSyncService
  ) {}

  ngOnInit(): void {
    this.handleCallback();
  }

  private async handleCallback(): Promise<void> {
    const queryParams = this.route.snapshot.queryParams;
    const provider = queryParams['provider'];
    const jwtToken = queryParams['token'];
    const auth = queryParams['auth'];

    console.log(`🎵 LoginCallback: Handling ${provider} connection`);
    this.statusMessage = `Connecting to ${provider}...`;
    this.isLoading = true;

    try {
      // Store the token in localStorage
      localStorage.setItem(APP_CONSTANTS.STORAGE_KEYS.MAIN_TOKEN, jwtToken);
      console.log('✅ LoginCallback: JWT token stored in localStorage');

      // Handle the service connection
      if (provider === 'discogs') {
        console.log('🎵 LoginCallback: Connecting to Discogs service');
        try {
          await this.musicSync.connectDiscogs();
          console.log('✅ LoginCallback: Discogs connection successful');
          this.statusMessage = 'Discogs connected successfully!';
        } catch (error: any) {
          console.error('❌ LoginCallback: Error connecting to Discogs:', error);

          // Handle specific error cases
          if (error.status === 400) {
            this.statusMessage = 'No Discogs account found. Please connect your Discogs account first.';
          } else if (error.status === 500) {
            this.statusMessage = 'Unable to connect to Discogs at this time. Please try again later.';
          } else {
            this.statusMessage = 'Failed to connect to Discogs. Please try again.';
          }

          throw error; // Re-throw to be caught by outer catch
        }
      } else if (provider === 'spotify') {
        console.log('🎵 LoginCallback: Spotify login completed, redirecting to dashboard');
        this.statusMessage = 'Spotify login successful! Redirecting to dashboard...';
        // No need to connect Spotify here - user will do it from dashboard
      }

      // Wait for the status message to be visible
      await new Promise(resolve => setTimeout(resolve, APP_CONSTANTS.ANIMATIONS.REDIRECT_DELAY));

      // Redirect to dashboard
      console.log('🔄 LoginCallback: Redirecting to dashboard');
      this.router.navigate([APP_CONSTANTS.ROUTES.DASHBOARD], {
        queryParams: {
          auth: 'success',
          service: provider
        }
      });

    } catch (error: any) {
      console.error(`❌ LoginCallback: Error in handleCallback:`, error);
      this.isLoading = false;
      this.errorMessage = this.statusMessage || 'An error occurred while connecting to the service.';

      // Wait before redirecting on error
      await new Promise(resolve => setTimeout(resolve, APP_CONSTANTS.ANIMATIONS.REDIRECT_DELAY));
      this.router.navigate([APP_CONSTANTS.ROUTES.DASHBOARD], {
        queryParams: {
          auth: 'error',
          service: provider,
          error: this.errorMessage
        }
      });
    }
  }

  goToLogin(): void {
    this.router.navigate([APP_CONSTANTS.ROUTES.LOGIN]);
  }
}
